import { useState, ReactElement, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getCookie } from 'cookies-next';
import Drawer from '@/components/common/Drawer';
import Button from '@/components/common/Button';
import Cart from '@/components/common/Cart';
import ItemCard from '../common/ItemCard';
import { useRouter } from 'next/router';
import NavLink from '../common/NavLink';
import { useCartStore } from 'stores/useCartStore';
import formatCurrency from '@/utilities/formatCurrency';
import { useCategoryStore } from 'stores/useCategoryStore';
import { Collection } from '@/types/collections';

function convertSecondsToTimer(seconds: number) {
  if (seconds < 0) return '0:00';
  let minutes = Math.floor(seconds / 60);
  let remainingSeconds: number | string = seconds % 60;
  if (remainingSeconds < 10) {
    remainingSeconds = '0' + remainingSeconds;
  }
  return minutes + ':' + remainingSeconds;
}

async function fetchCart() {
  try {
    const response = await fetch('/api/cart/get', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const data = await response.json();
    useCartStore.setState({
      cart: data.cart,
      expires: Math.floor(
        (new Date(data.expires).getTime() - new Date().getTime()) / 1000
      )
    });
  } catch (error) {
    console.error('Failed to fetch cart:', error);
  }
}

const CategorySubMenuSection = ({ category, setIsOpen }: {
  category: any
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  return (
    <>
      <div className="my-3">
        <div className="flex flex-row">
          <div className="flex-grow">
            <Link href={`/discover/${category.handle}`} passHref>
              <a className="uppercase font-normal text-lg">{category.title.value}</a>
            </Link>
          </div>
          <div onClick={() => setIsCollapsed(!isCollapsed)}>
            <Image
              alt={!open ? 'open-icon' : 'close-icon'}
              width={16}
              height={16}
              src={!isCollapsed ? '/icons/minus.svg' : '/icons/plus.svg'}
            />
          </div>
        </div>
        {!isCollapsed && <div>
          {category.collections.references.nodes.map((collection: Collection) => (
            <div key={collection.id} onClick={() => setIsOpen(false)} className="my-3">
              <Link href={`/brand/${collection.handle}`} passHref>
                <a className="font-light text-md">{collection.title}</a>
              </Link>
            </div>
          ))}
        </div>}
      </div>
      <hr className="border-silver border-b-0 my-5" />
    </>
  )
}

const MobileDiscoveryShopSubmenu = ({ categories, setIsOpen }: {
  categories: any[];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  return (
    <>
      {categories.map((category) => <CategorySubMenuSection key={category.id} category={category} setIsOpen={setIsOpen} />)}
    </>
  )
}


const MobileNavigation = ({
  isOpen,
  setIsOpen,
  handleSignOut,
  accessToken,
  categories
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSignOut: () => void;
  accessToken: string | null;
  categories: any[];
}): ReactElement => {
  const [isSubMenuOpen, setSubMenuOpen] = useState<boolean>(false);
  return (
    <Drawer
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isSubMenuOpen={isSubMenuOpen}
      setSubMenuOpen={setSubMenuOpen}
      className="h-[calc(100%-80px)] mt-[80px] z-50"
      submenuTitle="Member Store"
      submenu={<MobileDiscoveryShopSubmenu categories={categories} setIsOpen={setIsOpen} />}
    >
      <div className="flex flex-col text-white gap-3 pt-2 h-full">
        <div onClick={() => setIsOpen(false)}>
          <Link href="/" passHref>
            <a className="uppercase font-light text-xl">New Drop</a>
          </Link>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        <div onClick={() => {
          setSubMenuOpen(true)
        }}>
          <a className="uppercase font-light text-xl cursor-pointer">Member Store</a>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        {accessToken && (
          <>
            <div onClick={() => setIsOpen(false)}>
              <Link href="/account" passHref>
                <a className="uppercase font-light text-xl">My Account</a>
              </Link>
            </div>
            <hr className="border-silver border-b-0 my-2" />
          </>
        )}
        <div onClick={() => setIsOpen(false)}>
          <Link href="/blog" passHref>
            <a className="uppercase font-light text-xl">Blog</a>
          </Link>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        <div onClick={() => setIsOpen(false)}>
          <Link href="/membership" passHref>
            <a className="uppercase font-light text-xl">Membership</a>
          </Link>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        <div onClick={() => setIsOpen(false)}>
          <Link href="/contact" passHref>
            <a className="uppercase font-light text-xl">Contact</a>
          </Link>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        <div onClick={() => setIsOpen(false)}>
          <Link href="/faq" passHref>
            <a className="uppercase font-light text-xl">FAQ</a>
          </Link>
        </div>
        <hr className="border-silver border-b-0 my-2" />
        {accessToken && (
          <>
            <div className="flex-grow" />
            <div
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <Link href="#" passHref>
                <a className="uppercase font-light text-xl" onClick={handleSignOut}>
                  Sign Out
                </a>
              </Link>
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}

const Header = (): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [dropdownImageUrl, setDropdownImageUrl] = useState<string>('/images/dropdown-image.webp');
  const router = useRouter();
  const cart = useCartStore((state) => state.cart);
  const expires = useCartStore((state) => state.expires);
  const [updatingCart, setUpdatingCart] = useState(false)

  const categories = useCategoryStore((state) => state.categories);

  useEffect(() => {
    const accessToken = getCookie('accessToken') as string | null;
    if (accessToken) {
      setAccessToken(accessToken);
    }
  }, []);

  const activeNavLinkClassNames =
    'lowercase italic text-[20px] font-header -mt-1';

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (expires > 0) {
      interval = setInterval(() => {
        useCartStore.getState().tick();
      }, 1000);
    } else {
      fetchCart();
    }

    return () => {
      clearInterval(interval);
    };
  }, [expires]);

  useEffect(() => {
    if (cart) {
      let totalQuantity = 0;
      cart.lines.nodes.forEach((item) => {
        totalQuantity += item.quantity;
      });
      setTotalQuantity(totalQuantity);
    }
  }, [cart]);

  async function handleSignOut() {
    fetch('/api/auth/signout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    }).then(() => {
      router.push('/login');
    });
  }

  return (
    <>
      <header className="sticky top-0 bg-white shadow-md z-30 h-auto">
        <nav className="flex lg:grid lg:grid-cols-8 items-center lg:px-24 text-onyx max-h-[80px] justify-between">
          <div className="w-full h-full hidden lg:flex lg:items-center lg:col-span-3 gap-8">
            <NavLink
              href="/"
              className="flex items-center hover:font-normal h-full uppercase gap-3"
              exact
              activeClassName={activeNavLinkClassNames}
            >
              <span>New&nbsp;Drop</span>
            </NavLink>
            <div className="flex items-center hover:font-normal h-full cursor-default gap-3 group">
              <span className={`uppercase ${router.asPath.startsWith('/discover/') && activeNavLinkClassNames}`}>
                Member&nbsp;Store
              </span>
              <div className="absolute top-[75px] left-0 mt-2 w-full bg-white shadow-sm z-10 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200">
                <div className="lg:grid lg:grid-cols-5">
                  <div className="lg:col-span-1 relative">
                    <Image
                      src={dropdownImageUrl}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      className="w-full"
                      alt="Brand image"
                    />
                  </div>
                  <div className="lg:col-span-4">
                    <div className="p-4">
                      <div className="flex flex-row w-full min-h-[150px]">
                        {categories.map((category) => (
                          <div key={category.id}>
                            <Link href={`/discover/${category.handle}`} passHref>
                              <a className="inline-block px-4 py-2 text-sm uppercase font-light hover:font-normal">
                                {category.title.value}
                              </a>
                            </Link>
                            {category.collections.references.nodes.map((collection: any) => (
                              <div key={collection.id}>
                                <Link href={`/brand/${collection.handle}`} passHref>
                                  <a
                                    onMouseEnter={() => setDropdownImageUrl(collection.image.url)}
                                    className="inline-block px-4 py-1 text-sm font-light hover:font-normal"
                                  >
                                    {collection.title}
                                  </a>
                                </Link>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-black text-center py-2">
                      <span className="uppercase text-white tracking-wide font-light text-sm">
                        Discover and try full size products at up to 60% off
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <NavLink
              href="/membership"
              className="flex items-center hover:font-normal h-full uppercase gap-3"
              exact
              activeClassName={activeNavLinkClassNames}
            >
              <span>Membership</span>
            </NavLink>
          </div>
          <div className="flex lg:hidden items-center">
            <Button type="submit" onClick={() => setIsOpen(!isOpen)}>
              <Image
                className=" lg:hidden"
                src="/hamburger.svg"
                width={24}
                height={24}
                alt="Menu"
              />
            </Button>
          </div>
          <div className="lg:col-span-2 mx-auto">
            <Link href="/" passHref>
              <a className="block h-full">
                <Image
                  src="/daily-polished-logo.svg"
                  width={115}
                  height={80}
                  alt="Daily Polished Logo"
                />
              </a>
            </Link>
          </div>
          <div className="justify-end lg:flex lg:col-span-3 gap-3 mr-4">
            {accessToken ? (
              <div className="hidden lg:flex lg:col-span-3 justify-end gap-3">
                <NavLink
                  href="/account"
                  activeClassName={activeNavLinkClassNames}
                  className="uppercase hover:font-normal"
                >
                  My Account
                </NavLink>
              </div>
            ) : (
              <NavLink
                href="/login"
                exact
                activeClassName={activeNavLinkClassNames}
                className="uppercase hover:font-normal"
              >
                <span className="hidden lg:inline">Sign In</span>
                <span className="lg:hidden">
                  <Image
                    src="/account.svg"
                    width={24}
                    height={24}
                    alt="Account"
                  />
                </span>
              </NavLink>
            )}
            <button
              className="uppercase lg:flex lg:col-span-3 justify-end ml-2"
              aria-disabled="true"
              onClick={() => useCartStore.getState().toggleCart()}
            >
              <span className="hidden lg:inline font-light">
                MY BAG ({totalQuantity})
              </span>
              <span className="lg:hidden">
                <Image src="/bag.svg" width={24} height={24} alt="Cart" />
              </span>
            </button>
          </div>
        </nav>
        <p className="text-sm text-center bg-black text-white py-2 uppercase">
          Think outside the beauty box
        </p>
      </header>
      <MobileNavigation
        accessToken={accessToken}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleSignOut={handleSignOut}
        categories={categories}
      />
      <Cart>
        <div className="flex flex-col text-white bg-metal relative h-full">
          <header className="sticky top-0 px-6 z-30">
            <div className="absolute top-4 right-4 cursor-pointer">
              <button
                className="cursor-pointer"
                onClick={() => useCartStore.setState({ isOpen: false })}
              >
                <Image
                  src="/icons/cross.svg"
                  width={24}
                  height={24}
                  alt="Close Button"
                />
              </button>
            </div>
            <div className="flex items-end pt-5">
              <h1 className="text-4xl mr-4">My Cart</h1>
              <p className="pb-0.5">({totalQuantity} items)</p>
            </div>
            {cart?.totalQuantity && cart?.totalQuantity > 0 ? (
              <>
                {' '}
                <div className="flex mt-3">
                  <div className="mr-2">
                    <Image
                      src="/exclamation.svg"
                      alt="cart"
                      width={14}
                      height={20}
                    />
                  </div>
                  <p className="text-coral text-sm">
                    You have {convertSecondsToTimer(expires)} left to validate
                    your cart
                  </p>
                </div>
                <hr className="border border-cloud mt-4" />
              </>
            ) : null}
          </header>
          <div className="px-6 max-h-full overflow-y-auto pb-36">
            {cart?.lines?.nodes.map((item) => (
              <ItemCard
                key={item.id}
                itemName={item.merchandise.product.title}
                variantName={item.merchandise.title}
                itemCost={item.merchandise.price?.amount}
                itemImage={
                  item.merchandise.product.images.nodes[0].url
                }
                variantId={item.merchandise.id}
                lineId={item.id}
                quantity={item.quantity}
                updatingCart={updatingCart}
                setUpdatingCart={setUpdatingCart}
                availableQuantity={item.merchandise.quantityAvailable}
                // @ts-expect-error Because this is a metafield
                maxQuantity={item.merchandise.product?.max?.value ?? 1}
                productId={item.merchandise.product.id}
              />
            ))}
          </div>
          {!(cart?.totalQuantity && cart?.totalQuantity > 0) && (
            <div className="text-center h-full mt-24">Cart is empty</div>
          )}
          {cart?.totalQuantity && cart?.totalQuantity > 0 ? (
            <div className="px-6 fixed w-full bg-onyx bottom-0 py-4">
              <div className="flex justify-between mt-2">
                <p className="text-3xl font-header">Subtotal</p>
                <p>
                  {formatCurrency(Number(cart?.cost.totalAmount?.amount))}
                </p>
              </div>
              <a href={cart?.checkoutUrl}>
                <button className="border-onyx py-4 px-2 uppercase w-full mx-auto mt-5 bg-cloud text-onyx">
                  Checkout
                </button>
              </a>
            </div>
          ) : null}
        </div>
      </Cart>
    </>
  );
};

export default Header;
